import React from 'react';
import { Box, styled, keyframes } from '@mui/material';
import ShimmerEffect from './ShimmerEffect';

// Generated by Copilot

const shimmerAnimation = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

interface ShimmerChartCardProps {
  /** Width of the chart card */
  width?: string | number;
  /** Height of the chart card */
  height?: string | number;
  /** Minimum height of the chart card */
  minHeight?: string | number;
  /** Additional CSS class */
  className?: string;
  /** Show center text area (like percentage) */
  showCenterText?: boolean;
  /** Show legend/details area */
  showLegend?: boolean;
  /** Number of legend items */
  legendItems?: number;
  /** Custom chart size */
  chartSize?: string | number;
  /** Show back button area */
  showBackButton?: boolean;
  /** Chart variant */
  variant?: 'pie' | 'donut' | 'bar' | 'line';
}

const ChartCardContainer = styled(Box)(({ theme }) => ({
  background: '#fff',
  borderRadius: '10px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  padding: '16px 30px 16px 30px',
  position: 'relative',
  width: '100%',
  minHeight: '220px',
}));

const TitleShimmer = styled(Box)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  marginBottom: '10px',
  letterSpacing: '0.5px',
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row', // Changed to row for side-by-side layout
  alignItems: 'flex-start', // Align to top
  width: '100%',
  gap: '20px', // Add gap between chart and legend
}));

const ChartContainer = styled(Box)<{ chartSize?: string | number }>(({ chartSize }) => ({
  position: 'relative',
  width: chartSize || '190px',
  height: chartSize || '190px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0, // Prevent chart from shrinking
}));

const CenterTextContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

const LegendContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  flex: 1, // Take remaining space
  minWidth: '120px',
  paddingTop: '20px', // Add some top padding to align with chart
}));

const LegendItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  margin: '4px 0',
}));

const ShimmerChartCard: React.FC<ShimmerChartCardProps> = ({
  width = '91%',
  height,
  minHeight = '220px',
  className = '',
  showCenterText = true,
  showLegend = true,
  legendItems = 4,
  chartSize = '150px',
  showBackButton = false,
  variant = 'donut',
}) => {
  const renderChart = () => {
    switch (variant) {
      case 'pie':
      case 'donut':
        return (
          <ShimmerEffect
            variant="circular"
            width={chartSize}
            height={chartSize}
          />
        );
      case 'bar':
        return (
          <Box sx={{ 
            width: chartSize, 
            height: chartSize, 
            display: 'flex', 
            alignItems: 'end', 
            gap: '4px',
            justifyContent: 'center'
          }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <ShimmerEffect
                key={index}
                width="20px"
                height={`${60 + Math.random() * 80}px`}
                borderRadius="2px"
              />
            ))}
          </Box>
        );
      case 'line':
        return (
          <ShimmerEffect
            width={chartSize}
            height={chartSize}
            borderRadius="8px"
          />
        );
      default:
        return (
          <ShimmerEffect
            variant="circular"
            width={chartSize}
            height={chartSize}
          />
        );
    }
  };

  return (
    <ChartCardContainer
      className={`shimmer-chart-card ${className}`}
      sx={{
        width,
        height:100,
        minHeight,
      }}
    >
      {/* Header with title and optional back button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '10px' 
      }}>
        <TitleShimmer>
          <ShimmerEffect width="150px" height="16px" />
        </TitleShimmer>
        {showBackButton && (
          <ShimmerEffect
            variant="circular"
            width="24px"
            height="24px"
          />
        )}
      </Box>

      <ContentContainer>
        {/* Chart Area */}
        <ChartContainer chartSize={chartSize}>
          {renderChart()}
          
          {/* Center Text (for donut/pie charts) */}
          {showCenterText && (variant === 'donut' || variant === 'pie') && (
            <CenterTextContainer>
              <ShimmerEffect width="40px" height="32px" />
              <ShimmerEffect width="60px" height="16px" />
            </CenterTextContainer>
          )}
        </ChartContainer>

        {/* Legend/Details Area - Now on the right side */}
        {showLegend && (
          <LegendContainer>
            {Array.from({ length: legendItems }).map((_, index) => (
              <LegendItem key={index}>
                <ShimmerEffect
                  variant="circular"
                  width="13px"
                  height="13px"
                />
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  flex: 1 
                }}>
                  <ShimmerEffect 
                    width={`${60 + Math.random() * 40}px`} 
                    height="14px" 
                  />
                  <ShimmerEffect width="20px" height="14px" />
                </Box>
              </LegendItem>
            ))}
          </LegendContainer>
        )}
      </ContentContainer>
    </ChartCardContainer>
  );
};

export default ShimmerChartCard;

// Usage examples:

/**
 * Basic Usage Examples:
 * 
 * // Basic donut chart shimmer (matches LifecycleStatusCard)
 * <ShimmerChartCard />
 * 
 * // Pie chart shimmer
 * <ShimmerChartCard variant="pie" />
 * 
 * // Bar chart shimmer
 * <ShimmerChartCard variant="bar" />
 * 
 * // Line chart shimmer
 * <ShimmerChartCard variant="line" />
 * 
 * // Custom configuration
 * <ShimmerChartCard 
 *   variant="donut"
 *   chartSize="200px"
 *   legendItems={5}
 *   showBackButton={true}
 * />
 * 
 * // Without center text (for pie charts)
 * <ShimmerChartCard 
 *   variant="pie" 
 *   showCenterText={false} 
 * />
 * 
 * // Without legend
 * <ShimmerChartCard 
 *   showLegend={false} 
 * />
 * 
 * // Multiple charts in a grid
 * <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
 *   <ShimmerChartCard variant="donut" />
 *   <ShimmerChartCard variant="bar" />
 * </Box>
 */
